/**
 * ChecklistGrid — the 17-point compliance summary panel.
 *
 * Renders the auditor's 17-row Excel checklist as a one-glance grid:
 * each row shows compliant / violated / inconclusive / deviation-justified
 * status. Clicking a row deep-links to the corresponding finding.
 *
 * Data sources:
 *   - validationApi.getLoanFileView() → findings array
 *   - losApi.get()                    → LOS data presence (for inconclusive
 *                                       state when no LOS data uploaded)
 *
 * The grid maps rule IDs to checklist points using
 * CHECKLIST_RULE_IDS_IN_ORDER from shared. When a rule produced a
 * finding, that finding's verdict drives the row's state. When a rule
 * produced no finding, the row is "compliant" (rules emit findings only
 * for violations + LLM-disagreement cases, not for compliant outcomes —
 * see validation-core/orchestrator.ts).
 *
 * Score: simple percentage of (compliant + deviation-justified) rows over
 * the total evaluable rows. Inconclusive rows are excluded from the
 * denominator. Production scoring (Sprint 7) will be weight-based.
 */

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ShieldCheck, CheckCircle2, XCircle, AlertTriangle, MinusCircle, FileQuestion,
  Download, Eye,
} from 'lucide-react';
import { Card, SectionLabel } from '@/acc-ui/primitives/Layout';
import { Badge } from '@/acc-ui/primitives/Badge';
import { Button } from '@/acc-ui/primitives/Button';
import { validationApi } from '@/api/validation-api';
import { losApi } from '@/api/los-api';
import { ingestionApi } from '@/api/ingestion-api';
import { parsingApi } from '@/api/parsing-api';
import { bankingVerificationApi } from '@/api/banking-verification-api';
import { usePermission } from '@/auth/use-permission';
import {
  VALIDATION_VIEW,
  LOS_VIEW,
  SEVERITY,
  type FindingDTO,
  type Severity,
} from '@loanaudit/shared';
import { Link } from 'react-router-dom';
import { generateAndOpenReport } from './audit-report';
import { EvidenceDrawer, type RowStatus as DrawerRowStatus } from './EvidenceDrawer';

// -------------------------------------------------------------------------
// Static checklist row metadata. Order and labels mirror the auditor's
// Excel sheet (17 logical points, fulfilled by 15 underlying rule IDs —
// points 8/9/10 share los.novel_check_performed).
// -------------------------------------------------------------------------

interface ChecklistRow {
  point: number;
  /** Short label for the grid cell. */
  label: string;
  /** Tooltip / detail line. */
  description: string;
  /** Rule IDs that contribute to this row. First match wins. */
  ruleIds: readonly string[];
  /** LOS-screen path the auditor would look at, shown in the row tooltip. */
  losPath: string;
}

const CHECKLIST_ROWS: readonly ChecklistRow[] = [
  {
    point: 1,
    label: 'Documents Required',
    description: 'Min 2 KYC, CAM, CFA, BQS report, Legal & Technical report',
    ruleIds: ['los.documents_required', 'uc1.mandatory_doc.kyc'],
    losPath: 'LOS → Document → Digi Document Checklist',
  },
  {
    point: 2,
    label: 'API Status Details',
    description: 'PAN, Aadhar, eMandate, CKYC, PAN Profile all Success+Validated',
    ruleIds: ['los.api_statuses_validated'],
    losPath: 'LOS → Audit → API Status Details',
  },
  {
    point: 3,
    label: 'Dedupe Screening',
    description: 'Posidex Validated for every applicant',
    ruleIds: ['los.dedupe_all_applicants_validated'],
    losPath: 'LOS → Application Details → View Dedupe Review',
  },
  {
    point: 4,
    label: 'CIBIL Score',
    description: 'Meets product-specific threshold (PL 740, Disha HL 651, etc.)',
    ruleIds: ['los.cibil_score_meets_threshold'],
    losPath: 'LOS → Decision → CIBIL Report View',
  },
  {
    point: 5,
    label: 'Hunter Verification',
    description: 'Clean — or approved deviation on file',
    ruleIds: ['los.hunter_clean_or_deviation'],
    losPath: 'LOS → Audit → Hunter Review View',
  },
  {
    point: 6,
    label: 'BQS Check',
    description: 'No Match — or approved deviation on file',
    ruleIds: ['los.bqs_no_match_or_deviation'],
    losPath: 'LOS → Underwriting → Recommended/Final Decision View',
  },
  {
    point: 7,
    label: 'RCU Status',
    description: 'Positive outcome — or approved deviation on file',
    ruleIds: ['los.rcu_status_valid'],
    losPath: 'LOS → Verification → RCU Verification View',
  },
  {
    point: 8,
    label: 'Income Proof (PL/BL)',
    description: 'Salary credits verified via Novel Check banking',
    ruleIds: ['los.novel_check_performed'],
    losPath: 'LOS → Document → Banking Analysis',
  },
  {
    point: 9,
    label: 'Income Proof (ML/HL)',
    description: 'Banking surrogate / GST program — CFA verified',
    ruleIds: ['los.novel_check_performed'],
    losPath: 'LOS → Verification → Cash Flow Analysis',
  },
  {
    point: 10,
    label: 'Novel Check',
    description: 'Banking analysis performed',
    ruleIds: ['los.novel_check_performed'],
    losPath: 'LOS → Document → Banking Analysis',
  },
  {
    point: 11,
    label: 'Loan Eligibility',
    description: 'Amount, ROI, tenor, EMI match CAM',
    ruleIds: ['los.loan_eligibility_matches_cam'],
    losPath: 'LOS → Detailed Data Entry → Loan Details',
  },
  {
    point: 12,
    label: 'Company Cap',
    description: 'Sanctioned ≤ company-category cap',
    ruleIds: ['los.loan_within_company_cap'],
    losPath: 'LOS → Detailed Data Entry → Applicant → Company Categorization',
  },
  {
    point: 13,
    label: 'Deviation Matrix',
    description: 'All deviations approved with documented mitigants',
    ruleIds: ['los.all_deviations_approved'],
    losPath: 'LOS → Deviation → Approve Deviation View',
  },
  {
    point: 14,
    label: 'Validity of Sanction',
    description: 'Disbursement within 30 days of sanction',
    ruleIds: ['los.disbursement_within_validity'],
    losPath: 'LOS → Audit → Activity History',
  },
  {
    point: 15,
    label: 'Sanction Conditions',
    description: 'Conditions documented in LOS',
    ruleIds: ['los.sanction_conditions_match_cam'],
    losPath: 'LOS → Underwriting → Sanction Condition View',
  },
  {
    point: 16,
    label: 'Loan Agreement',
    description: 'E-Agreement Success + all metadata fields populated',
    ruleIds: ['los.e_agreement_metadata_complete'],
    losPath: 'LOS → WF Activities → Initiate E-Agreement View',
  },
  {
    point: 17,
    label: 'Key Fact Statement',
    description: 'KFS present alongside Loan Agreement',
    ruleIds: ['los.kfs_attached_to_loan_agreement'],
    losPath: 'LOS → WF Activities → Initiate E-Agreement View',
  },
] as const;

// -------------------------------------------------------------------------
// Row state derivation
// -------------------------------------------------------------------------

type RowState =
  | 'compliant'           // no finding fired → silently passing
  | 'deviation_justified' // finding fired with title containing "deviation approved"
  | 'violated'            // finding fired, violation
  | 'inconclusive'        // finding fired, verdict inconclusive (e.g. no LOS data)
  | 'no_los';             // no LOS data uploaded yet → entire grid inconclusive

interface DerivedRow extends ChecklistRow {
  state: RowState;
  finding: FindingDTO | null;
  severity: Severity | null;
}

function deriveRow(
  row: ChecklistRow,
  findingsByRuleId: Map<string, FindingDTO>,
  hasLosData: boolean,
): DerivedRow {
  // First find a finding for any of this row's rule IDs. First hit wins.
  let finding: FindingDTO | null = null;
  for (const ruleId of row.ruleIds) {
    const f = findingsByRuleId.get(ruleId);
    if (f) {
      finding = f;
      break;
    }
  }

  if (finding) {
    // A finding means the rule produced something. Two possibilities:
    // - "deviation approved" wording in the title → row is justified
    // - everything else → violated (rules don't emit findings for clean
    //   compliant outcomes; only for issues, including LLM disagreements)
    const titleLower = (finding.title ?? '').toLowerCase();
    if (titleLower.includes('deviation approved')) {
      return { ...row, state: 'deviation_justified', finding, severity: finding.severity };
    }
    // Some rules report inconclusive — these have severity=null typically.
    if (!finding.severity) {
      return { ...row, state: 'inconclusive', finding, severity: null };
    }
    return { ...row, state: 'violated', finding, severity: finding.severity };
  }

  // No finding for this row. If LOS data is missing entirely, every
  // LOS-side row should show as inconclusive — but the deterministic
  // rules already emit inconclusive findings when ctx.losData is null,
  // so finding will usually be non-null. We use the no-LOS state mostly
  // as an empty-state fallback.
  if (!hasLosData && row.ruleIds.some((r) => r.startsWith('los.'))) {
    return { ...row, state: 'no_los', finding: null, severity: null };
  }
  return { ...row, state: 'compliant', finding: null, severity: null };
}

// -------------------------------------------------------------------------
// Score
// -------------------------------------------------------------------------

function computeScore(rows: readonly DerivedRow[]): {
  passed: number;
  failed: number;
  deviation: number;
  inconclusive: number;
  noLos: number;
  score: number; // 0..100
} {
  let passed = 0, failed = 0, deviation = 0, inconclusive = 0, noLos = 0;
  for (const r of rows) {
    if (r.state === 'compliant') passed++;
    else if (r.state === 'violated') failed++;
    else if (r.state === 'deviation_justified') deviation++;
    else if (r.state === 'inconclusive') inconclusive++;
    else noLos++;
  }
  // Score: percentage of evaluable rows that are NOT violated. Both clean
  // pass and deviation-justified count as compliant. Inconclusive and
  // no-LOS rows are excluded from the denominator so they don't drag the
  // score down for absence of data.
  const evaluable = passed + deviation + failed;
  const score = evaluable === 0 ? 0 : Math.round(((passed + deviation) / evaluable) * 100);
  return { passed, failed, deviation, inconclusive, noLos, score };
}

// -------------------------------------------------------------------------
// Visuals
// -------------------------------------------------------------------------

function StateIcon({ state }: { state: RowState }) {
  if (state === 'compliant') return <CheckCircle2 className="w-4 h-4 text-emerald-700" />;
  if (state === 'deviation_justified')
    return <ShieldCheck className="w-4 h-4 text-amber-700" />;
  if (state === 'violated') return <XCircle className="w-4 h-4 text-red-700" />;
  if (state === 'inconclusive') return <MinusCircle className="w-4 h-4 text-amber-700" />;
  return <FileQuestion className="w-4 h-4 text-slate-500" />;
}

function StateLabel({ state, severity }: { state: RowState; severity: Severity | null }) {
  if (state === 'compliant') {
    return <Badge tone="success">Passed</Badge>;
  }
  if (state === 'deviation_justified') {
    return <Badge tone="info" className="bg-amber-50 text-amber-800">Approved with Deviation</Badge>;
  }
  if (state === 'violated') {
    const tone = severity === SEVERITY.CRITICAL || severity === SEVERITY.HIGH
      ? 'danger' : 'warning';
    return <Badge tone={tone}>Failed{severity ? ` · ${severity}` : ''}</Badge>;
  }
  if (state === 'inconclusive') {
    return <Badge tone="warning">Inconclusive</Badge>;
  }
  return <Badge tone="muted">No LOS data</Badge>;
}

function ScoreRing({ score, failed }: { score: number; failed: number }) {
  // Three tiers: green ≥ 90, amber 70–89, red < 70.
  // A single critical failure (failed > 0) downgrades green to amber.
  const tier = failed > 0
    ? (score >= 70 ? 'amber' : 'red')
    : (score >= 90 ? 'green' : score >= 70 ? 'amber' : 'red');
  const colors = {
    green: { fg: '#047857', bg: '#d1fae5', ring: 'border-emerald-600' },
    amber: { fg: '#92400e', bg: '#fef3c7', ring: 'border-amber-600' },
    red:   { fg: '#991b1b', bg: '#fee2e2', ring: 'border-red-600' },
  }[tier];
  return (
    <div
      className={`inline-flex items-center justify-center w-20 h-20 rounded-full border-4 ${colors.ring}`}
      style={{ background: colors.bg }}
    >
      <div className="text-center">
        <div className="text-2xl font-bold leading-none" style={{ color: colors.fg }}>
          {score}
        </div>
        <div className="text-micro uppercase tracking-wide" style={{ color: colors.fg, opacity: 0.7 }}>
          / 100
        </div>
      </div>
    </div>
  );
}

// -------------------------------------------------------------------------
// Main component
// -------------------------------------------------------------------------

export function ChecklistGrid({ loanFileId }: { loanFileId: string }) {
  const { has } = usePermission();
  const canSeeValidation = has(VALIDATION_VIEW);
  const canSeeLos = has(LOS_VIEW);

  // Pull validation view (gives us findings) and LOS data attached flag.
  const validationQuery = useQuery({
    queryKey: ['validationView', loanFileId],
    queryFn: () => validationApi.getLoanFileView(loanFileId),
    enabled: canSeeValidation,
    refetchInterval: (q) => {
      // Keep polling while a run is in progress so the grid updates live.
      const state = q.state.data?.state;
      if (!state) return false;
      return state === 'validation_in_progress' ? 4000 : false;
    },
  });

  const losQuery = useQuery({
    queryKey: ['losData', loanFileId],
    queryFn: () => losApi.get(loanFileId),
    enabled: canSeeLos,
  });

  // Same key the parent uses → React Query dedupes, no extra request.
  const loanFileQuery = useQuery({
    queryKey: ['loanFile', loanFileId],
    queryFn: () => ingestionApi.get(loanFileId),
    enabled: canSeeValidation,
  });

  // Parsing view — needed by Evidence Drawer's Point 11 (Loan Eligibility)
  // to look up page numbers per extracted field for the "→ Page N" jump
  // buttons on the CAM cross-check.
  const parsingQuery = useQuery({
    queryKey: ['parsingView', loanFileId],
    queryFn: () => parsingApi.getLoanFileView(loanFileId),
    enabled: canSeeValidation,
    // Parsing data doesn't change often — cache aggressively.
    staleTime: 60_000,
  });

  // Banking verification — latest completed row. Used to enrich Evidence
  // Drawer points 8-10 and to add the Banking Verification Evidence section
  // to the downloaded audit report.
  const bankingVerifQuery = useQuery({
    queryKey: ['bankingVerifications', loanFileId],
    queryFn: () => bankingVerificationApi.list(loanFileId),
    enabled: canSeeValidation,
    staleTime: 30_000,
  });
  const latestBankingVerif =
    bankingVerifQuery.data?.verifications?.find((v) => v.status === 'completed') ?? null;

  // Evidence drawer state — which point is being viewed, or null if closed.
  const [evidencePoint, setEvidencePoint] = useState<number | null>(null);

  // Build map ruleId → finding (latest only, validation API already
  // returns open findings from the latest run).
  const findingsByRuleId = useMemo(() => {
    const m = new Map<string, FindingDTO>();
    for (const f of validationQuery.data?.findings ?? []) {
      // First-occurrence wins. The API returns severity-sorted; we want
      // the most-severe finding per rule.
      if (!m.has(f.ruleId)) m.set(f.ruleId, f);
    }
    return m;
  }, [validationQuery.data?.findings]);

  const hasLosData = Boolean(losQuery.data);

  const derived = useMemo<DerivedRow[]>(
    () => CHECKLIST_ROWS.map((row) => deriveRow(row, findingsByRuleId, hasLosData)),
    [findingsByRuleId, hasLosData],
  );

  const stats = useMemo(() => computeScore(derived), [derived]);

  if (!canSeeValidation) return null;

  // Loading or never-run state
  if (validationQuery.isLoading) {
    return (
      <Card>
        <div className="text-text-tertiary text-label">Loading 17-point checklist…</div>
      </Card>
    );
  }

  const noRunYet = !validationQuery.data?.latestRun;
  if (noRunYet) {
    return (
      <Card>
        <div className="flex items-start gap-3">
          <div className="rounded-md bg-slate-100 text-slate-700 p-1.5 shrink-0">
            <ShieldCheck className="w-4 h-4" />
          </div>
          <div>
            <SectionLabel>17-Point Compliance Audit</SectionLabel>
            <div className="mt-1 text-label text-text-secondary">
              Validation hasn't run yet for this loan file. Once parsing completes (and LOS data is uploaded), the 17-point grid will appear here automatically.
            </div>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card>
      {/* Header: score + summary stats + download */}
      <div className="flex items-start gap-5 flex-wrap">
        <ScoreRing score={stats.score} failed={stats.failed} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <ShieldCheck className="w-4 h-4 text-brand-blue" />
            <SectionLabel className="!m-0">17-Point Compliance Audit</SectionLabel>
            {stats.noLos > 0 && (
              <Badge tone="warning" className="ml-1">LOS data missing</Badge>
            )}
          </div>
          <div className="mt-2 flex items-center gap-3 flex-wrap text-label">
            <span className="inline-flex items-center gap-1.5">
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-700" />
              <span className="font-semibold text-text-primary">{stats.passed}</span>
              <span className="text-text-tertiary">passed</span>
            </span>
            {stats.deviation > 0 && (
              <span className="inline-flex items-center gap-1.5">
                <ShieldCheck className="w-3.5 h-3.5 text-amber-700" />
                <span className="font-semibold text-text-primary">{stats.deviation}</span>
                <span className="text-text-tertiary">deviation-justified</span>
              </span>
            )}
            <span className="inline-flex items-center gap-1.5">
              <XCircle className="w-3.5 h-3.5 text-red-700" />
              <span className="font-semibold text-text-primary">{stats.failed}</span>
              <span className="text-text-tertiary">failed</span>
            </span>
            {(stats.inconclusive > 0 || stats.noLos > 0) && (
              <span className="inline-flex items-center gap-1.5">
                <MinusCircle className="w-3.5 h-3.5 text-amber-700" />
                <span className="font-semibold text-text-primary">
                  {stats.inconclusive + stats.noLos}
                </span>
                <span className="text-text-tertiary">inconclusive</span>
              </span>
            )}
          </div>
          <div className="mt-1 text-micro text-text-tertiary">
            Score: {stats.passed + stats.deviation} of {stats.passed + stats.deviation + stats.failed} evaluable checks pass
            {stats.inconclusive + stats.noLos > 0 && (
              <> · {stats.inconclusive + stats.noLos} check(s) inconclusive (excluded from score)</>
            )}
          </div>
        </div>

        {/* Download report — opens a print-ready, downloadable HTML report
            in a new tab. The user can either print to PDF or download as HTML. */}
        <div className="shrink-0">
          <Button
            variant="secondary"
            onClick={() => {
              const lf = loanFileQuery.data;
              if (!lf) return;
              generateAndOpenReport({
                loanFile: {
                  id: lf.id,
                  loanNumber: lf.loanNumber,
                  borrowerName: lf.borrowerName,
                  productCode: lf.productCode,
                  branchCode: lf.branchCode,
                  state: lf.state,
                },
                findings: validationQuery.data?.findings ?? [],
                losData: losQuery.data ?? null,
                validatedAt: validationQuery.data?.latestRun?.finishedAt ?? null,
                bankingVerification: latestBankingVerif,
              });
            }}
            disabled={!loanFileQuery.data || validationQuery.isLoading}
            title="Open a print-ready 17-point audit report in a new tab"
          >
            <Download className="w-4 h-4 mr-1.5" />
            Download Report
          </Button>
        </div>
      </div>

      {/* The 17-row grid */}
      <div className="mt-4 border border-glass-hairline rounded-md overflow-hidden">
        <ul className="divide-y divide-glass-hairline">
          {derived.map((row) => (
            <ChecklistRowItem
              key={row.point}
              row={row}
              onViewEvidence={(point) => setEvidencePoint(point)}
            />
          ))}
        </ul>
      </div>

      <div className="mt-3 text-micro text-text-tertiary">
        Click <span className="font-medium">Evidence</span> on any row to see the exact data the rule evaluated.
        Click <span className="font-medium">Review →</span> on failed rows to open the finding with resolution actions.
      </div>

      {/* Evidence drawer — slides out from the right when a row's "Evidence"
          button is clicked. Renders point-specific evidence for all 17 checks. */}
      {evidencePoint !== null && (() => {
        const row = derived.find((r) => r.point === evidencePoint);
        if (!row) return null;
        // Map our internal RowState to the drawer's RowStatus type.
        const status: DrawerRowStatus =
          row.state === 'compliant' ? 'compliant'
          : row.state === 'deviation_justified' ? 'deviation_justified'
          : row.state === 'violated' ? 'violated'
          : row.state === 'inconclusive' ? 'inconclusive'
          : 'no_los';
        return (
          <EvidenceDrawer
            open={true}
            onClose={() => setEvidencePoint(null)}
            loanFileId={loanFileId}
            point={row.point}
            label={row.label}
            status={status}
            finding={row.finding}
            losData={losQuery.data ?? null}
            documents={(loanFileQuery.data?.documents ?? []).map((d) => ({
              id: d.id,
              originalFileName: d.originalFileName,
              documentType: d.documentType,
            }))}
            losPath={row.losPath}
            parsingView={parsingQuery.data ?? null}
            bankingVerification={latestBankingVerif}
          />
        );
      })()}
    </Card>
  );
}

function ChecklistRowItem({
  row, onViewEvidence,
}: {
  row: DerivedRow;
  onViewEvidence: (point: number) => void;
}) {
  // The whole row is no longer a single link, since each row now ends with
  // a "View Evidence" button. Failed rows still get a deep-link to the
  // finding page (separate icon button next to the evidence one) for the
  // auditor to act on it.
  return (
    <li title={row.losPath}>
      <div className="flex items-center gap-3 px-3 py-2 hover:bg-slate-50/40 transition-colors">
        <div className="w-6 text-center text-caption font-semibold text-text-tertiary tabular-nums shrink-0">
          {row.point}
        </div>
        <div className="shrink-0">
          <StateIcon state={row.state} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-label font-medium text-text-primary truncate">
            {row.label}
          </div>
          <div className="text-micro text-text-tertiary truncate">
            {row.finding?.description ?? row.description}
          </div>
        </div>
        <div className="shrink-0">
          <StateLabel state={row.state} severity={row.severity} />
        </div>
        <div className="shrink-0 flex items-center gap-1">
          <button
            type="button"
            onClick={() => onViewEvidence(row.point)}
            className="inline-flex items-center gap-1 text-micro font-medium text-brand-blue hover:bg-brand-blue/[0.06] px-2 py-1 rounded-md transition-colors"
            title="View evidence for this check"
            aria-label={`View evidence for point ${row.point}`}
          >
            <Eye className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Evidence</span>
          </button>
          {row.state === 'violated' && row.finding && (
            <Link
              to={`/findings/${row.finding.id}`}
              className="text-micro font-medium text-red-700 hover:bg-red-50 px-2 py-1 rounded-md transition-colors"
              title="Open finding for review"
            >
              Review →
            </Link>
          )}
        </div>
      </div>
    </li>
  );
}

// Quiet unused-import warnings when icons aren't always used
void AlertTriangle;
